import Box from "@mui/material/Box";
import type {ColumnDef} from "@tanstack/react-table";
import type {ContentCategory} from "crqlar-react-common/dist/models/content/contentCategory.model";
import {ContentCategoryService} from "crqlar-react-common/dist/services/contentCategory/contentCategory.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import SettingsListPage from "../layout/SettingsListPage";

const ContentCategoriesList = () => {
    useTrackScreenVisit('system content categories');

    const navigate = useNavigate();
    const {t} = useTranslation();

    const columns = useMemo<ColumnDef<ContentCategory>[]>(() => [
        {
            id: 'name',
            header: t('Name'),
            accessorFn: category => category.name,
            cell: ({row}) => <Box>{row.original.name || '-'}</Box>,
            size: 320,
            meta: {defaultActive: true, isServerSortable: true, grow: true}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: category => category.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 300,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: category => category.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        }
    ], [t]);

    const controller = useServerTable({
        tableId: 'system-content-categories',
        columns,
        getRowId: category => category.id!,
        onRowClick: category => navigate(`/system/contentCategories/${category.id}`),
        initialSorting: [{id: 'id', desc: true}],
        queryFn: ({pagination, searchQuery, sorting}) => ContentCategoryService.getAllContentCategories({
            limit: {
                offset: pagination.pageIndex * pagination.pageSize,
                limit: pagination.pageSize,
                sort: sorting ? [sorting] : undefined
            },
            query: searchQuery || undefined
        }).then(page => ({
            content: page.content ?? [],
            totalElements: page.totalElements ?? 0
        }))
    });

    return (
        <SettingsListPage title={t('Content categories')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSearch/>
                        <ServerTableSort/>
                        <ServerTableFullscreen/>
                        <ServerTableColumns/>
                        <ServerTableCreate
                            label={t('Create content category')}
                            onClick={() => navigate('/system/contentCategories/new')}
                        />
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </SettingsListPage>
    );
};

export default ContentCategoriesList;
